import type { Account, Account_insert, AccountInvite, AccountInvite_insert, AccountMembership, AccountMembership_insert, FormSubmission, FormSubmission_insert, Guardian, Guardian_insert, Project, Project_insert, User, User_insert } from "./types-ts";
import { AccountSchema, Account_insertSchema, AccountInviteSchema, AccountInvite_insertSchema, AccountMembershipSchema, AccountMembership_insertSchema, FormSubmissionSchema, FormSubmission_insertSchema, GuardianSchema, Guardian_insertSchema, ProjectSchema, Project_insertSchema, UserSchema, User_insertSchema } from "./types-zod";
import type { ZodType } from "zod";

export interface TypeMapping
{
    name:string;
    ts?:string;
    zod?:string;
    convo?:string;
    sql?:string;
}

export interface PropDef
{
    name:string;
    type:TypeMapping;
    primary?:boolean;
    description?:string;
    sqlDef?:string;
    optional?:boolean;
    hasDefault?:boolean;
    isArray?:boolean;
    arrayDimensions?:number;
}

export interface TypeDef<
    TValue extends Record<string,any>=Record<string,any>,
    TInsert extends Record<string,any>=Record<string,any>
>{


    name:string;
    description?:string;
    type:'type'|'enum';
    primaryKey:(keyof TValue) & (keyof TInsert);
    sqlTable?:string;
    sqlSchema?:string;
    zodSchema?:ZodType;
    zodInsertSchema?:ZodType;
    props:PropDef[];
}

export const typeDefs={
    Account: {
        name: "Account",
        description: "An account (tenant) within the system",
        primaryKey: "id",
        sqlSchema: "public",
        sqlTable: "account",
        type: "type",
        props: [
            {
                name: "created_at",
                description: "When the account was created",
                hasDefault: true,
                sqlDef: "created_at timestamptz not null default now()",
                type: {
                    name: "string",
                    sql: "timestamptz",
                    ts: "string"
                }
            },
            {
                name: "hero_image_path",
                description: "Path to the account's hero image in the 'accounts' storage bucket",
                optional: true,
                sqlDef: "hero_image_path text",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "id",
                description: "Unique id of the account",
                hasDefault: true,
                primary: true,
                sqlDef: "id uuid not null default gen_random_uuid ()",
                type: {
                    name: "string",
                    sql: "uuid",
                    ts: "string"
                }
            },
            {
                name: "is_default",
                hasDefault: true,
                sqlDef: "is_default boolean not null default false",
                type: {
                    name: "boolean",
                    sql: "bool",
                    ts: "boolean"
                }
            },
            {
                name: "logo_image_path",
                description: "Path to the account's logo image in the 'accounts' storage bucket",
                optional: true,
                sqlDef: "logo_image_path text",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "name",
                description: "Display name of the account",
                sqlDef: "name text not null",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            }
        ],
        zodSchema: AccountSchema,
        zodInsertSchema: Account_insertSchema,
    } as TypeDef<Account,Account_insert> satisfies TypeDef<Account,Account_insert>,

    AccountInvite: {
        name: "AccountInvite",
        description: "An invitation to join an account (used for accept-invite flow)",
        primaryKey: "id",
        sqlSchema: "public",
        sqlTable: "account_invite",
        type: "type",
        props: [
            {
                name: "accepted_at",
                description: "When the invite was accepted (if accepted)",
                optional: true,
                sqlDef: "accepted_at timestamptz",
                type: {
                    name: "string",
                    sql: "timestamptz",
                    ts: "string"
                }
            },
            {
                name: "accepted_by_user_id",
                description: "The user who accepted the invite (if accepted)",
                optional: true,
                sqlDef: "accepted_by_user_id uuid",
                type: {
                    name: "string",
                    sql: "uuid",
                    ts: "string"
                }
            },
            {
                name: "account_id",
                description: "The account the invite belongs to",
                sqlDef: "account_id uuid not null",
                type: {
                    name: "string",
                    sql: "uuid",
                    ts: "string"
                }
            },
            {
                name: "created_at",
                description: "When the invite was created",
                hasDefault: true,
                sqlDef: "created_at timestamptz not null default now()",
                type: {
                    name: "string",
                    sql: "timestamptz",
                    ts: "string"
                }
            },
            {
                name: "expires_at",
                description: "When this invite expires (if set)",
                optional: true,
                sqlDef: "expires_at timestamptz",
                type: {
                    name: "string",
                    sql: "timestamptz",
                    ts: "string"
                }
            },
            {
                name: "id",
                description: "Unique id of the account invite",
                hasDefault: true,
                primary: true,
                sqlDef: "id uuid not null default gen_random_uuid ()",
                type: {
                    name: "string",
                    sql: "uuid",
                    ts: "string"
                }
            },
            {
                name: "invite_code",
                description: "Unique invite code used to accept or decline the invite",
                sqlDef: "invite_code text not null",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "invited_by_user_id",
                description: "The user who created the invite",
                optional: true,
                sqlDef: "invited_by_user_id uuid",
                type: {
                    name: "string",
                    sql: "uuid",
                    ts: "string"
                }
            },
            {
                name: "invited_email",
                description: "The email address being invited",
                sqlDef: "invited_email text not null",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "role",
                description: "Proposed role of the invited user within the account",
                hasDefault: true,
                sqlDef: "role public.user_role not null default 'guest'",
                type: {
                    name: "string",
                    sql: "public",
                    ts: "string"
                }
            }
        ],
        zodSchema: AccountInviteSchema,
        zodInsertSchema: AccountInvite_insertSchema,
    } as TypeDef<AccountInvite,AccountInvite_insert> satisfies TypeDef<AccountInvite,AccountInvite_insert>,

    AccountMembership: {
        name: "AccountMembership",
        description: "Links a user to an account with a role",
        primaryKey: "id",
        sqlSchema: "public",
        sqlTable: "account_membership",
        type: "type",
        props: [
            {
                name: "account_id",
                description: "The account the user belongs to",
                sqlDef: "account_id uuid not null",
                type: {
                    name: "string",
                    sql: "uuid",
                    ts: "string"
                }
            },
            {
                name: "created_at",
                description: "When the membership was created",
                hasDefault: true,
                sqlDef: "created_at timestamptz not null default now()",
                type: {
                    name: "string",
                    sql: "timestamptz",
                    ts: "string"
                }
            },
            {
                name: "id",
                description: "Unique id of the account membership",
                hasDefault: true,
                primary: true,
                sqlDef: "id uuid not null default gen_random_uuid ()",
                type: {
                    name: "string",
                    sql: "uuid",
                    ts: "string"
                }
            },
            {
                name: "last_accessed_at",
                description: "The last time the user accessed this account",
                hasDefault: true,
                sqlDef: "last_accessed_at timestamptz not null default now()",
                type: {
                    name: "string",
                    sql: "timestamptz",
                    ts: "string"
                }
            },
            {
                name: "role",
                description: "The role of the user within the account",
                hasDefault: true,
                sqlDef: "role public.user_role not null default 'guest'",
                type: {
                    name: "string",
                    sql: "public",
                    ts: "string"
                }
            },
            {
                name: "user_id",
                description: "The user who is a member of the account",
                sqlDef: "user_id uuid not null",
                type: {
                    name: "string",
                    sql: "uuid",
                    ts: "string"
                }
            }
        ],
        zodSchema: AccountMembershipSchema,
        zodInsertSchema: AccountMembership_insertSchema,
    } as TypeDef<AccountMembership,AccountMembership_insert> satisfies TypeDef<AccountMembership,AccountMembership_insert>,

    FormSubmission: {
        name: "FormSubmission",
        primaryKey: "id",
        sqlSchema: "public",
        sqlTable: "form_submission",
        type: "type",
        props: [
            {
                name: "created_at",
                hasDefault: true,
                sqlDef: "created_at timestamptz not null default now()",
                type: {
                    name: "string",
                    sql: "timestamptz",
                    ts: "string"
                }
            },
            {
                name: "data",
                sqlDef: "data jsonb not null",
                type: {
                    name: "json",
                    convo: "map",
                    sql: "jsonb",
                    ts: "Record<string,any>",
                    zod: "z.record(z.string(),z.any())"
                }
            },
            {
                name: "id",
                hasDefault: true,
                primary: true,
                sqlDef: "id uuid not null default gen_random_uuid ()",
                type: {
                    name: "string",
                    sql: "uuid",
                    ts: "string"
                }
            },
            {
                name: "name",
                hasDefault: true,
                sqlDef: "name text not null default 'default'",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            }
        ],
        zodSchema: FormSubmissionSchema,
        zodInsertSchema: FormSubmission_insertSchema,
    } as TypeDef<FormSubmission,FormSubmission_insert> satisfies TypeDef<FormSubmission,FormSubmission_insert>,

    Guardian: {
        name: "Guardian",
        description: "A person (content-creating Guardian) who leads or represents initiatives",
        primaryKey: "id",
        sqlSchema: "public",
        sqlTable: "guardian",
        type: "type",
        props: [
            {
                name: "account_id",
                description: "Owning account of this guardian",
                sqlDef: "account_id uuid not null",
                type: {
                    name: "string",
                    sql: "uuid",
                    ts: "string"
                }
            },
            {
                name: "created_at",
                description: "When the guardian was created",
                hasDefault: true,
                sqlDef: "created_at timestamptz not null default now()",
                type: {
                    name: "string",
                    sql: "timestamptz",
                    ts: "string"
                }
            },
            {
                name: "created_by_user_id",
                description: "The user who created this guardian (if available)",
                optional: true,
                sqlDef: "created_by_user_id uuid",
                type: {
                    name: "string",
                    sql: "uuid",
                    ts: "string"
                }
            },
            {
                name: "description",
                description: "Short description of the guardian",
                optional: true,
                sqlDef: "description text",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "id",
                description: "Unique id of the guardian",
                hasDefault: true,
                primary: true,
                sqlDef: "id uuid not null default gen_random_uuid ()",
                type: {
                    name: "string",
                    sql: "uuid",
                    ts: "string"
                }
            },
            {
                name: "image_path",
                description: "Primary image path for the guardian",
                optional: true,
                sqlDef: "image_path text",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "image_paths",
                arrayDimensions: 1,
                description: "Additional image paths for media gallery",
                hasDefault: true,
                isArray: true,
                sqlDef: "image_paths text[] not null default '{}'",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "is_featured",
                description: "Whether the guardian should be featured on the landing page",
                hasDefault: true,
                sqlDef: "is_featured boolean not null default false",
                type: {
                    name: "boolean",
                    sql: "bool",
                    ts: "boolean"
                }
            },
            {
                name: "is_public",
                description: "Whether the guardian is visible across accounts",
                hasDefault: true,
                sqlDef: "is_public boolean not null default false",
                type: {
                    name: "boolean",
                    sql: "bool",
                    ts: "boolean"
                }
            },
            {
                name: "long_description",
                description: "Long-form markdown description of the guardian",
                optional: true,
                sqlDef: "long_description text",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "name",
                description: "Display name of the guardian",
                sqlDef: "name text not null",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "social_links",
                arrayDimensions: 1,
                description: "Social links (URLs) associated with the guardian",
                hasDefault: true,
                isArray: true,
                sqlDef: "social_links text[] not null default '{}'",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "status",
                description: "Moderation status of the guardian",
                hasDefault: true,
                sqlDef: "status public.content_status not null default 'pending'",
                type: {
                    name: "string",
                    sql: "public",
                    ts: "string"
                }
            },
            {
                name: "tags",
                arrayDimensions: 1,
                description: "Tags used for discovery and related content",
                hasDefault: true,
                isArray: true,
                sqlDef: "tags text[] not null default '{}'",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "video_path",
                description: "Optional video path for the guardian",
                optional: true,
                sqlDef: "video_path text",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "what_they_love",
                description: "what they love",
                optional: true,
                sqlDef: "what_they_love text",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            }
        ],
        zodSchema: GuardianSchema,
        zodInsertSchema: Guardian_insertSchema,
    } as TypeDef<Guardian,Guardian_insert> satisfies TypeDef<Guardian,Guardian_insert>,

    Project: {
        name: "Project",
        description: "A project (initiative) associated with a guardian",
        primaryKey: "id",
        sqlSchema: "public",
        sqlTable: "project",
        type: "type",
        props: [
            {
                name: "account_id",
                description: "Owning account of this project",
                sqlDef: "account_id uuid not null",
                type: {
                    name: "string",
                    sql: "uuid",
                    ts: "string"
                }
            },
            {
                name: "created_at",
                description: "When the project was created",
                hasDefault: true,
                sqlDef: "created_at timestamptz not null default now()",
                type: {
                    name: "string",
                    sql: "timestamptz",
                    ts: "string"
                }
            },
            {
                name: "created_by_user_id",
                description: "The user who created this project (if available)",
                optional: true,
                sqlDef: "created_by_user_id uuid",
                type: {
                    name: "string",
                    sql: "uuid",
                    ts: "string"
                }
            },
            {
                name: "description",
                description: "Short description of the project",
                optional: true,
                sqlDef: "description text",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "guardian_id",
                description: "The guardian who owns this project",
                sqlDef: "guardian_id uuid not null",
                type: {
                    name: "string",
                    sql: "uuid",
                    ts: "string"
                }
            },
            {
                name: "id",
                description: "Unique id of the project",
                hasDefault: true,
                primary: true,
                sqlDef: "id uuid not null default gen_random_uuid ()",
                type: {
                    name: "string",
                    sql: "uuid",
                    ts: "string"
                }
            },
            {
                name: "image_path",
                description: "Primary image path for the project",
                optional: true,
                sqlDef: "image_path text",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "image_paths",
                arrayDimensions: 1,
                description: "Additional image paths for media gallery",
                hasDefault: true,
                isArray: true,
                sqlDef: "image_paths text[] not null default '{}'",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "is_featured",
                description: "Whether the project should be featured on the landing page",
                hasDefault: true,
                sqlDef: "is_featured boolean not null default false",
                type: {
                    name: "boolean",
                    sql: "bool",
                    ts: "boolean"
                }
            },
            {
                name: "is_public",
                description: "Whether the project is visible across accounts",
                hasDefault: true,
                sqlDef: "is_public boolean not null default false",
                type: {
                    name: "boolean",
                    sql: "bool",
                    ts: "boolean"
                }
            },
            {
                name: "lat",
                description: "Latitude of the project's location",
                optional: true,
                sqlDef: "lat double precision",
                type: {
                    name: "number",
                    sql: "float8",
                    ts: "number"
                }
            },
            {
                name: "lng",
                description: "Longitude of the project's location",
                optional: true,
                sqlDef: "lng double precision",
                type: {
                    name: "number",
                    sql: "float8",
                    ts: "number"
                }
            },
            {
                name: "long_description",
                description: "Long-form markdown description of the project",
                optional: true,
                sqlDef: "long_description text",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "name",
                description: "Display name of the project",
                sqlDef: "name text not null",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "needs",
                arrayDimensions: 1,
                description: "Project needs (actionable items)",
                hasDefault: true,
                isArray: true,
                sqlDef: "needs text[] not null default '{}'",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "slogan",
                description: "Short slogan/value statement of the project",
                optional: true,
                sqlDef: "slogan text",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "social_links",
                arrayDimensions: 1,
                description: "Social links (URLs) associated with the project",
                hasDefault: true,
                isArray: true,
                sqlDef: "social_links text[] not null default '{}'",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "status",
                description: "Moderation status of the project",
                hasDefault: true,
                sqlDef: "status public.content_status not null default 'pending'",
                type: {
                    name: "string",
                    sql: "public",
                    ts: "string"
                }
            },
            {
                name: "tags",
                arrayDimensions: 1,
                description: "Tags used for discovery and related content",
                hasDefault: true,
                isArray: true,
                sqlDef: "tags text[] not null default '{}'",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "video_path",
                description: "Optional video path for the project",
                optional: true,
                sqlDef: "video_path text",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "website_url",
                description: "Project website URL",
                optional: true,
                sqlDef: "website_url text",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            }
        ],
        zodSchema: ProjectSchema,
        zodInsertSchema: Project_insertSchema,
    } as TypeDef<Project,Project_insert> satisfies TypeDef<Project,Project_insert>,

    User: {
        name: "User",
        description: "A user",
        primaryKey: "id",
        sqlSchema: "public",
        sqlTable: "user",
        type: "type",
        props: [
            {
                name: "created_at",
                description: "When the user was created",
                hasDefault: true,
                sqlDef: "created_at timestamptz not null default now()",
                type: {
                    name: "string",
                    sql: "timestamptz",
                    ts: "string"
                }
            },
            {
                name: "email",
                description: "Email address of the user",
                sqlDef: "email text not null",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "hero_image_path",
                description: "Path to the user's hero image in the 'accounts' storage bucket",
                optional: true,
                sqlDef: "hero_image_path text",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "id",
                description: "Unique id of the user",
                hasDefault: true,
                primary: true,
                sqlDef: "id uuid not null default gen_random_uuid ()",
                type: {
                    name: "string",
                    sql: "uuid",
                    ts: "string"
                }
            },
            {
                name: "name",
                description: "Full name of the user",
                sqlDef: "name text not null",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            },
            {
                name: "profile_image_path",
                description: "Path to the user's profile image in the 'accounts' storage bucket",
                optional: true,
                sqlDef: "profile_image_path text",
                type: {
                    name: "string",
                    sql: "text",
                    ts: "string"
                }
            }
        ],
        zodSchema: UserSchema,
        zodInsertSchema: User_insertSchema,
    } as TypeDef<User,User_insert> satisfies TypeDef<User,User_insert>,


} as const;

export const typeList=[
    typeDefs.Account,
    typeDefs.AccountInvite,
    typeDefs.AccountMembership,
    typeDefs.FormSubmission,
    typeDefs.Guardian,
    typeDefs.Project,
    typeDefs.User,
] as const;
